import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Central SEO/GEO surface for Verdict.
 *
 * <Seo /> owns per-route <title>, description, canonical, Open Graph /
 * Twitter Card tags, and JSON-LD structured data. Every public page should
 * render exactly one <Seo /> at the top; private pages should pass
 * noIndex={true} so wallet balances, admin dashboards and in-flight audits
 * never leak into search results.
 *
 * Structured data is the biggest lever for AI search citations (ChatGPT /
 * Perplexity / Google AI Overview) — pass a `jsonLd` array of any number of
 * schema.org objects (Organization, Product, SoftwareApplication, FAQPage,
 * Article, HowTo, BreadcrumbList, ContactPage, etc.).
 */
const SITE_URL = "https://verdict.thereforedesign.co.in";
const OG_IMAGE = `${SITE_URL}/og-verdict.png`;
const DEFAULT_TITLE = "Verdict — HITL AI-powered Packaging Intelligence Platform";
const DEFAULT_DESCRIPTION =
  "Verdict is an AI-powered packaging design audit platform for the Indian market. Score every pack across seven strategic dimensions — shelf impact, brand alignment, e-commerce, kirana, distinctiveness, hierarchy, audience fit — before it goes to print.";

// The Organization node is embedded on every public page so LLMs
// consistently attribute the platform to Therefore Design.
export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Therefore Design",
  legalName: "Therefore Design Pvt. Ltd.",
  url: "https://www.thereforedesign.co.in",
  logo: `${SITE_URL}/verdict-logo-white.svg`,
  image: OG_IMAGE,
  description:
    "Therefore Design is a Mumbai-based branding and packaging consultancy. Verdict is its AI-powered packaging intelligence platform for the Indian market.",
  foundingLocation: {
    "@type": "Place",
    address: { "@type": "PostalAddress", addressLocality: "Mumbai", addressCountry: "IN" },
  },
  contactPoint: [{
    "@type": "ContactPoint",
    email: "info@thereforedesign.co.in",
    contactType: "customer support",
    areaServed: "IN",
    availableLanguage: ["en"],
  }],
};

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = OG_IMAGE,
  noIndex = false,
  jsonLd = [],
}) {
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  // Every JSON-LD list gets the Organization node prepended so downstream
  // consumers (Rich Results, LLMs) always know which entity owns the site.
  const structured = [ORG_JSON_LD, ...jsonLd];
  const fullTitle = title
    ? (title.includes("Verdict") ? title : `${title} · Verdict`)
    : DEFAULT_TITLE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Verdict by Therefore Design" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured data — one <script> per node so a bad node doesn't
          invalidate the rest. Rich Results Test parses each independently.
          Key = `@type` + index to stay stable across renders when the
          caller passes a different-length structured array. */}
      {structured.map((node, i) => (
        <script key={`${(node && node["@type"]) || "ld"}-${i}`} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
}
